"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from "@tanstack/react-query";
import { Suspense, lazy, useState } from "react";
import { isMobile } from "../../../../../utils/web/isMobile.js";
import { COINBASE } from "../../../../../wallets/constants.js";
import { isEcosystemWallet } from "../../../../../wallets/ecosystem/is-ecosystem-wallet.js";
import { getInstalledWalletProviders } from "../../../../../wallets/injected/mipdStore.js";
import { iconSize } from "../../../../core/design-system/index.js";
import { useWalletInfo } from "../../../../core/utils/wallet.js";
import { getInjectedWalletLocale } from "../../../wallets/injected/locale/getInjectedWalletLocale.js";
import { GetStartedScreen } from "../../../wallets/shared/GetStartedScreen.js";
import { LoadingScreen } from "../../../wallets/shared/LoadingScreen.js";
import { WalletConnectConnection, WalletConnectStandaloneConnection, } from "../../../wallets/shared/WalletConnectConnection.js";
import { Spacer } from "../../components/Spacer.js";
import { Container, ModalHeader } from "../../components/basic.js";
import { Text } from "../../components/text.js";
import { AccentFailIcon } from "../icons/AccentFailIcon.js";
import { DeepLinkConnectUI } from "./DeepLinkConnectUI.js";
import { InjectedConnectUI } from "./InjectedConnectUI.js";
const CoinbaseSDKWalletConnectUI = /* @__PURE__ */ lazy(() => import("../../../wallets/shared/CoinbaseSDKConnection.js"));
const InAppWalletConnectUI = /* @__PURE__ */ lazy(() => import("../../../wallets/in-app/InAppWalletConnectUI.js"));
const EcosystemWalletConnectUI = /* @__PURE__ */ lazy(() => import("../../../wallets/ecosystem/EcosystemWalletConnectUI.js"));
/**
 * @internal
 */
export function AnyWalletConnectUI(props) {
    const [screen, setScreen] = useState("main");
    const { wallet } = props;
    const walletInfo = useWalletInfo(props.wallet.id);
    const localeId = props.connectLocale.id;
    const localeFnQuery = useQuery({
        queryKey: ["injectedWalletLocale", localeId, walletInfo.data?.name],
        queryFn: async () => {
            return getInjectedWalletLocale(localeId);
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });
    if (walletInfo.isLoading || !localeFnQuery.data) {
        return _jsx(LoadingScreen, {});
    }
    const localeFn = localeFnQuery.data;
    if (!walletInfo.data) {
        const injectedProvider = getInstalledWalletProviders().find((w) => w.info.rdns === wallet.id);
        // Unknown wallet id but it's injected
        if (injectedProvider) {
            return (_jsx(InjectedConnectUI, { wallet: props.wallet, walletName: injectedProvider.info.name, done: props.done, locale: localeFn(injectedProvider.info.name), onBack: props.onBack, chain: props.chain, client: props.client, size: props.size }));
        }
        // This will only happen if developer passes a wallet with unknown id and it's not injected
        // Adding a fallback UI just in case
        return (_jsxs(Container, { animate: "fadein", fullHeight: true, flex: "column", children: [_jsx(Container, { p: "lg", children: _jsx(ModalHeader, { title: "Not Supported", onBack: props.onBack }) }), _jsxs(Container, { flex: "column", expand: true, center: "both", p: "lg", style: {
                        minHeight: "300px",
                    }, children: [_jsx(AccentFailIcon, { size: iconSize["3xl"] }), _jsx(Spacer, { y: "lg" }), _jsx(Text, { color: "primaryText", center: true, children: "Wallet is not supported" }), _jsx(Spacer, { y: "xxl" })] })] }));
    }
    const locale = localeFn(walletInfo.data.name);
    // if wallet can connect to injected wallet + wallet is injected
    const isInstalled = getInstalledWalletProviders().find((w) => w.info.rdns === walletInfo.data.rdns);
    if (screen === "get-started") {
        return (_jsx(GetStartedScreen, { locale: locale, wallet: props.wallet, walletInfo: walletInfo.data, onBack: () => {
                setScreen("main");
            }, client: props.client }));
    }
    if (walletInfo.data.deepLink &&
        !isInstalled &&
        wallet.getConfig()
            ?.preferDeepLink &&
        isMobile()) {
        return (_jsx(DeepLinkConnectUI, { wallet: props.wallet, walletInfo: walletInfo.data, deepLinkPrefix: walletInfo.data.deepLink.mobile, locale: locale, onGetStarted: () => {
                setScreen("get-started");
            }, onBack: props.onBack, client: props.client }));
    }
    if (walletInfo.data.rdns && isInstalled) {
        return (_jsx(InjectedConnectUI, { wallet: props.wallet, walletName: walletInfo.data.name, done: props.done, locale: locale, onGetStarted: () => {
                setScreen("get-started");
            }, onBack: props.onBack, chain: props.chain, client: props.client, size: props.size }));
    }
    // coinbase wallet sdk
    if (props.wallet.id === COINBASE) {
        return (_jsx(Suspense, { fallback: _jsx(LoadingScreen, {}), children: _jsx(CoinbaseSDKWalletConnectUI, { locale: locale, onGetStarted: () => {
                    setScreen("get-started");
                }, onBack: props.onBack, done: props.done, wallet: props.wallet, walletInfo: walletInfo.data, chain: props.chain, client: props.client, size: props.size }) }));
    }
    // wallet connect
    if (walletInfo.data.mobile.native || walletInfo.data.mobile.universal) {
        return (_jsx(WalletConnectConnection, { locale: locale, onGetStarted: () => {
                setScreen("get-started");
            }, onBack: props.onBack, done: props.done, wallet: props.wallet, walletInfo: walletInfo.data, chain: props.chain, chains: props.chains, client: props.client, size: props.size, walletConnect: props.walletConnect }));
    }
    // wallet connect
    if (props.wallet.id === "walletConnect") {
        return (_jsx(WalletConnectStandaloneConnection, { locale: locale, onBack: props.onBack, done: props.done, wallet: props.wallet, walletInfo: walletInfo.data, setModalVisibility: props.setModalVisibility, chain: props.chain, chains: props.chains, client: props.client, size: props.size, walletConnect: props.walletConnect }));
    }
    if (props.wallet.id === "inApp" || props.wallet.id === "embedded") {
        return (_jsx(Suspense, { fallback: _jsx(LoadingScreen, {}), children: _jsx(InAppWalletConnectUI, { wallet: props.wallet, done: props.done, goBack: props.onBack, chain: props.chain, client: props.client, size: props.size, walletConnect: props.walletConnect, connectLocale: props.connectLocale, meta: props.meta }) }));
    }
    if (isEcosystemWallet(props.wallet.id)) {
        return (_jsx(Suspense, { fallback: _jsx(LoadingScreen, {}), children: _jsx(EcosystemWalletConnectUI, { wallet: props.wallet, done: props.done, goBack: props.onBack, chain: props.chain, client: props.client, size: props.size, meta: props.meta, connectLocale: props.connectLocale }) }));
    }
    // if can't connect in any way - show get started screen
    return (_jsx(GetStartedScreen, { locale: locale, wallet: props.wallet, walletInfo: walletInfo.data, onBack: props.onBack, client: props.client }));
}
//# sourceMappingURL=AnyWalletConnectUI.js.map